import { useTranslation } from 'react-i18next'
import { CheckCircle, XCircle } from 'lucide-react'
import { Card, CardContent, Badge } from '@/components/v2/ui'
import { cn } from '@/lib/utils'
import type { TitleValidationResult } from '@/lib/amazon-title-validator'

interface TitleResultsTableProps {
  results: TitleValidationResult[]
}

export function TitleResultsTable({ results }: TitleResultsTableProps) {
  const { t } = useTranslation()
  if (results.length === 0) return null
  return (
    <Card className="rounded-xl shadow-sm border-neutral-200 dark:border-neutral-800">
      <CardContent className="p-6 space-y-3">
        <h2 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
          {t('amazonTitleTool.results.title')}
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <ResultsTableHeader />
            <tbody>
              {results.map((r) => <ResultRow key={r.title} result={r} />)}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

function ResultsTableHeader() {
  const { t } = useTranslation()
  return (
    <thead>
      <tr className="border-b border-neutral-200 dark:border-neutral-700 text-left">
        <th className="pb-2 pr-4 text-neutral-500 font-medium min-w-[300px]">{t('amazonTitleTool.results.columns.title')}</th>
        <th className="pb-2 pr-4 text-neutral-500 font-medium whitespace-nowrap">{t('amazonTitleTool.results.columns.status')}</th>
        <th className="pb-2 pr-4 text-neutral-500 font-medium whitespace-nowrap">{t('amazonTitleTool.results.columns.characters')}</th>
        <th className="pb-2 pr-4 text-neutral-500 font-medium whitespace-nowrap">{t('amazonTitleTool.results.columns.specialChars')}</th>
        <th className="pb-2 text-neutral-500 font-medium whitespace-nowrap">{t('amazonTitleTool.results.columns.problems')}</th>
      </tr>
    </thead>
  )
}

function StatusCell({ isValid }: { isValid: boolean }) {
  const { t } = useTranslation()
  return (
    <span className={cn('flex items-center gap-1 text-xs font-medium', isValid ? 'text-green-600' : 'text-red-500')}>
      {isValid
        ? <><CheckCircle className="h-4 w-4" />{t('amazonTitleTool.results.valid')}</>
        : <><XCircle className="h-4 w-4" />{t('amazonTitleTool.results.invalid')}</>}
    </span>
  )
}

function ProblemsBadgeList({ problems }: { problems: string[] }) {
  const { t } = useTranslation()
  if (problems.length === 0) return <span className="text-xs text-neutral-400">{t('amazonTitleTool.results.none')}</span>
  return (
    <div className="flex flex-wrap gap-1">
      {problems.map((p) => (
        <Badge key={p} variant="error" className="text-xs px-2 py-0.5">{p}</Badge>
      ))}
    </div>
  )
}

function ResultRow({ result }: { result: TitleValidationResult }) {
  const { t } = useTranslation()
  const problems = [
    ...result.repeatedKeywords, ...result.forbiddenMatches,
    ...result.allCapsWords, ...result.illegalChars,
  ]
  return (
    <tr className="border-b border-neutral-100 dark:border-neutral-800 last:border-0">
      <td className="py-3 pr-4 max-w-[400px] truncate text-neutral-700 dark:text-neutral-300">{result.title}</td>
      <td className="py-3 pr-4"><StatusCell isValid={result.isValid} /></td>
      <td className={cn('py-3 pr-4 text-xs font-mono', result.charCount > result.charMax ? 'text-red-500' : 'text-neutral-600 dark:text-neutral-400')}>
        {result.charCount}/{result.charMax}
      </td>
      <td className="py-3 pr-4 text-xs text-neutral-500">
        {result.illegalChars.length > 0 ? result.illegalChars.join(' ') : t('amazonTitleTool.results.none')}
      </td>
      <td className="py-3"><ProblemsBadgeList problems={problems} /></td>
    </tr>
  )
}
